import { type ExitKind, type InvestmentSetListItem } from '@d3/contract';

import { type ExitType } from '@entities/investment/@x/portfolio';
import type { CurrencyOption } from '@shared/config';

import type { PortfolioListItem } from '../model/portfolio-list';

const toCurrencyOption = (currency: string): CurrencyOption => (currency === 'USD' ? 'USD' : 'KRW');

/**
 * 계약의 회수 종류(ExitKind: partial · complete) → 레거시 뷰모델 ExitType.
 * investment 도메인이 계약으로 전환될 때 이 매핑은 해소된다.
 */
const toExitType = (kind: ExitKind): ExitType =>
  kind === 'complete' ? 'COMPLETE_EXIT' : 'PARTIAL_EXIT';

/**
 * 응답(@d3/contract indicator InvestmentSetListItem) → 목록 한 줄 뷰모델.
 * 계약이 중첩(portfolio · fund · investments · exits · internalManager)으로 바꾼 것을 화면 평면 필드로 흡수한다.
 * - idx ← portfolio.id, fundIdx ← fund.id, customOrder ← portfolio.order
 * - remainValuation 은 계약 Money(amount · exchangeRate) → 뷰모델의 금액 + 환율
 * - valuationHistory({date,amount}) → stockPriceHistory({when,amount}), revenueHistory({year,amount}) → {when: 'YYYY-01-01',amount}
 *   (연 매출 = 그 해 Q4 누적, DD-54 #7 — 해마다 같은 간격으로 놓이게 그 해 첫날로 둔다)
 */
export function toPortfolioListItem(item: InvestmentSetListItem): PortfolioListItem {
  return {
    idx: item.portfolio.id,
    portfolioName: item.portfolio.name,
    fundIdx: item.fund.id,
    fundName: item.fund.name,
    status: item.status,
    investD3History: item.investments.map((invest) => ({
      idx: invest.id,
      investAt: invest.investedAt,
      investVolume: invest.amount,
      exchangeRate: invest.exchangeRate ?? 1,
      exchangeUnit: toCurrencyOption(invest.currency),
    })),
    exitD3History: item.exits.map((exit) => ({
      id: exit.id,
      exitType: toExitType(exit.kind),
      exitAt: exit.exitedAt,
      exitVolume: exit.amount,
      exchangeRate: exit.exchangeRate ?? 1,
      exchangeUnit: toCurrencyOption(exit.currency),
    })),
    remainValuation: item.remainValuation.amount,
    remainValuationExchangeRate: item.remainValuation.exchangeRate,
    valuatedAt: item.valuatedAt,
    irr: item.irr,
    multiple: item.multiple,
    internalManagerName: item.internalManager?.name ?? null,
    revenueHistory: item.revenueHistory.map((point) => ({
      amount: point.amount,
      when: `${point.year}-01-01`,
    })),
    stockPriceHistory: item.valuationHistory.map((point) => ({
      amount: point.amount,
      when: point.date,
    })),
    defaultCurrency: item.portfolio.defaultCurrency === 'USD' ? 'USD' : 'KRW',
    customOrder: item.portfolio.order,
  };
}
